import { useRoute } from 'vue-router'
import { useCacheHttp } from '@/composables/utils/useCacheHttp.ts'
import { User, Channel } from 'src/store/models/index.js'
import { Bookable, Subject, Lesson, Grade, Teacher } from 'src/store/models/timetable/index.js'
import Classbooks from '@/api/classbooks.js'
import type { User as UserType } from '@/types/store/models/user'

type EntityInstance = User | Channel | Teacher | Grade | Lesson | Subject | Bookable | null

// Define a base interface for entity data
interface BaseEntityData {
  icon?: string
  color: string
  label?: string
  title?: string
  target?: string
  type?: string
}

// Define specific entity data interfaces
interface UserEntityData extends BaseEntityData {
  type: 'student' | 'teacher' | 'parent'
  label: string
  title: string
}

interface ChannelEntityData extends BaseEntityData {
  icon?: string
  color: string
  target?: string
  type?: string
}

interface TeacherEntityData extends BaseEntityData {
  type: 'teacher'
  label: string
  title: string
}

interface GradeEntityData extends BaseEntityData {
  type: 'grade'
  label: string
  title: string
}

interface LessonEntityData extends BaseEntityData {
  type: 'lesson'
  label: string
  title: string
}

interface SubjectEntityData extends BaseEntityData {
  type: 'subject'
  label: string
  title: string
}

interface BookableEntityData extends BaseEntityData {
  type: 'bookable'
  label: string
  title: string
}

interface FallbackEntityData extends BaseEntityData {
  type: 'fallback'
  label: string
  title: string
}

// Union type for all entity data
type EntityDataItem =
  | (UserEntityData & { entity: typeof User })
  | (ChannelEntityData & { entity: typeof Channel })
  | (TeacherEntityData & { entity: typeof Teacher })
  | (GradeEntityData & { entity: typeof Grade })
  | (LessonEntityData & { entity: typeof Lesson })
  | (SubjectEntityData & { entity: typeof Subject })
  | (BookableEntityData & { entity: typeof Bookable })
  | (FallbackEntityData & { entity: null })

// This generic will allow us to get the exact type of the requested entity
type EntityDataFor<T extends EntityInstance> =
  T extends User ? (UserEntityData & { entity: typeof User }) | (FallbackEntityData & { entity: null }) :
  T extends Channel ? (ChannelEntityData & { entity: typeof Channel }) | (FallbackEntityData & { entity: null }) :
  T extends Teacher ? (TeacherEntityData & { entity: typeof Teacher }) | (FallbackEntityData & { entity: null }) :
  T extends Grade ? (GradeEntityData & { entity: typeof Grade }) | (FallbackEntityData & { entity: null }) :
  T extends Lesson ? (LessonEntityData & { entity: typeof Lesson }) | (FallbackEntityData & { entity: null }) :
  T extends Subject ? (SubjectEntityData & { entity: typeof Subject }) | (FallbackEntityData & { entity: null }) :
  T extends Bookable ? (BookableEntityData & { entity: typeof Bookable }) | (FallbackEntityData & { entity: null }) :
  EntityDataItem

const entityData: EntityDataItem[] = [
  {
    entity: null,
    type: 'fallback',
    icon: 'icon-close-o',
    color: 'grey',
    label: 'timetable.components.choose_entity.entities.undefined',
    title: 'meta.displayname'
  },
  {
    entity: User,
    type: 'student',
    icon: 'icon-user-child',
    color: 'type-student',
    label: 'timetable.components.choose_entity.entities.student.singular',
    title: 'meta.displayname'
  },
  {
    entity: User,
    type: 'teacher',
    icon: 'icon-user-teacher',
    color: 'type-teacher',
    label: 'timetable.components.choose_entity.entities.teacher.singular',
    title: 'meta.displayname'
  },
  {
    entity: Teacher,
    type: 'teacher',
    icon: 'icon-user-teacher',
    color: 'type-teacher',
    label: 'timetable.components.choose_entity.entities.teacher.singular',
    title: 'meta.displayname'
  },
  {
    entity: User,
    type: 'parent',
    icon: 'icon-user-parent',
    color: 'type-parent',
    label: 'timetable.components.choose_entity.entities.parent.singular',
    title: 'meta.displayname'
  },
  {
    entity: Grade,
    type: 'grade',
    icon: 'icon-class',
    color: 'type-student',
    label: 'timetable.components.choose_entity.entities.grade.singular',
    title: 'meta.displayname'
  },
  {
    entity: Lesson,
    type: 'lesson',
    icon: 'icon-bell',
    color: 'type-teacher',
    label: 'timetable.components.choose_entity.entities.teacher.general',
    title: 'shortcut'
  },
  {
    entity: Subject,
    type: 'subject',
    icon: '',
    color: 'dark',
    label: 'timetable.components.choose_entity.entities.subject.singular',
    title: 'shortcut'
  },
  {
    entity: Bookable,
    type: 'bookable',
    icon: 'icon-key',
    color: 'purple',
    label: 'timetable.components.choose_entity.entities.bookable.singular',
    title: 'shortcut'
  },
  {
    entity: Channel,
    icon: 'icon-channel-parents-56',
    color: 'type-parent',
    target: 'parents'
  },
  {
    entity: Channel,
    icon: 'icon-channel-parents-56',
    color: 'type-parent',
    target: 'teachers-parents'
  },
  {
    entity: Channel,
    icon: 'icon-channel-students-56',
    color: 'type-student',
    target: 'students'
  },
  {
    entity: Channel,
    icon: 'icon-channel-custom-and-teachers-56',
    color: 'type-teacher',
    target: 'teachers'
  },
  {
    entity: Channel,
    icon: 'icon-channel-custom-and-teachers-56',
    color: 'gray-300',
    target: 'global'
  },
  {
    entity: Channel,
    icon: 'icon-channel-custom-and-teachers-56',
    color: 'gray-300',
    target: 'sv'
  },
  {
    entity: Channel,
    icon: 'icon-channel-custom-and-teachers-56',
    color: 'gray-300',
    target: 'sekretariat'
  },
  {
    entity: Channel,
    icon: 'icon-channel-custom-and-teachers-56',
    color: 'gray-300',
    type: 'custom'
  },
  {
    entity: Channel,
    target: 'courses',
    color: 'gray-300',
    type: 'custom'
  },
  {
    entity: Channel,
    target: 'course-parents',
    color: 'gray-300',
    type: 'custom'
  },
  {
    entity: Channel,
    color: 'gray-300',
    type: 'chat'
  }
]

export function useEntity() {
  const route = useRoute()
  const { cacheHttp } = useCacheHttp()

  const userProps = (user: UserType): (string | undefined)[] => {
    const data = getEntityData(new User(user))

    if (data.color?.includes('sdui-icon')) return [data.icon, data.color]
    return [data.icon, data.color, `${data.color}-light-bg`]
  }

  const getEntityData = <T extends EntityInstance>(entity: T): EntityDataFor<T> => {
    const result = getEntityInstance(entity) || entityData[0]
    return result as EntityDataFor<T>
  }

  const getEntityInstance = (entity: EntityInstance): EntityDataItem | null => {
    if (!entity || !(entity instanceof Object)) return null

    const matchingEntity = entityData.find(el => el.entity && entity instanceof el.entity &&
      !(entity instanceof User || entity instanceof Channel))

    if (matchingEntity) return matchingEntity

    if (!(entity instanceof User || entity instanceof Channel)) {
      return null
    }

    // object because user management doesnt have orm
    // @ts-expect-error TODO VUE3: wait until vuex-orm is typed properly(?)
    if (entity.type && entity instanceof User) {
      // @ts-expect-error TODO VUE3:  wait until vuex-orm is typed properly(?)
      const item = entityData.find(el => el.type === entity.type.toLowerCase())

      // @ts-expect-error TODO VUE3:  wait until vuex-orm is typed properly(?)
      if (item && entity.expire_at) {
        const icon = `${item.icon} expired-icon`
        return { ...item, icon: icon }
      }

      // @ts-expect-error TODO VUE3:  wait until vuex-orm is typed properly(?)
      if (item && entity.state === 'PENDING') {
        const icon = `${item.icon} pending-user`

        return {
          ...item,
          icon: icon
        }
      }

      return item || null
    }

    // @ts-expect-error TODO VUE3:  wait until vuex-orm is typed properly(?)
    if (entity instanceof User && entity.type) {
      // @ts-expect-error TODO VUE3:  wait until vuex-orm is typed properly(?)
      return entityData.find(el => el.type === entity.type?.toLowerCase()) || null
    }

    // @ts-expect-error TODO VUE3:  wait until vuex-orm is typed properly(?)
    if (entity instanceof Channel && entity.target) {
      // @ts-expect-error TODO VUE3:  wait until vuex-orm is typed properly(?)
      return entityData.find(el => el.target === entity.target.toLowerCase()) || null
    }

    // custom channels (individual groups) have a type but no target
    // @ts-expect-error TODO VUE3:  wait until vuex-orm is typed properly(?)
    if (entity instanceof Channel && entity.type) {
      // @ts-expect-error TODO VUE3:  wait until vuex-orm is typed properly(?)
      return entityData.find(el => el.type === entity.type.toLowerCase()) || null
    }

    return null
  }

  // TODO VUE 3: type endpoint return type
  const fetchEntity = async (entity: string, customId: string | null = null, classbookId: string | null = null) => {
    const entityEndpoint = () => {
      if (entity === 'grades') return Classbooks.gradesByIdUrl(route.params.classbook || classbookId, customId || '')
      if (entity === 'courses') return Classbooks.coursesByIdUrl(route.params.classbook || classbookId, customId || '')
      if (entity === 'users') return Classbooks.usersByIdUrl(route.params.classbook || classbookId, customId || '')
    }

    const { data } = await cacheHttp(entityEndpoint(), { timeout: import.meta.env.VITE_CLASSBOOK_EXTENDED_REQUEST_TIMEOUT })

    return data
  }

  return {
    userProps,
    getEntityData,
    getEntityInstance,
    fetchEntity,
  }
}
